// src/components/Experiment/DataTable.js
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Table, Form, Button, Badge } from 'react-bootstrap';
import ContextMenu from './ContextMenu';
import './DataTable.css';

const DataTable = ({ data, onCellsUpdate, onRangeSelection, isRangeSelectionMode }) => {
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [selectedRanges, setSelectedRanges] = useState([]); // Changed to array for multiple ranges
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [isSelecting, setIsSelecting] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [clipboard, setClipboard] = useState(null);
  const [ctrlPressed, setCtrlPressed] = useState(false); // Track CTRL key state
  const tableRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const autoScrollRef = useRef(null);

  const { data_matrix, columns_metadata, rows_metadata } = data;

  // Get column names in order (sample info first, then classes, then data columns)
  const getOrderedColumns = useCallback(() => {
    if (!columns_metadata) return [];

    const sampleCols = [];
    const classCols = [];
    const dataCols = [];

    Object.entries(columns_metadata).forEach(([colName, metadata]) => {
      if (metadata.type === 'sample_info') {
        sampleCols.push(colName);
      } else if (metadata.type === 'class') {
        classCols.push(colName);
      } else if (metadata.type === 'data') {
        dataCols.push(colName);
      }
    });

    // Sort data columns by job chronology using job_order field
    dataCols.sort((a, b) => {
      const metaA = columns_metadata[a];
      const metaB = columns_metadata[b];

      const orderA = metaA.job_order !== undefined ? metaA.job_order : metaA.job_id;
      const orderB = metaB.job_order !== undefined ? metaB.job_order : metaB.job_id;

      if (orderA !== orderB) {
        const result = orderA - orderB;
        return result;
      }

      const channelOrder = { 'R': 0, 'G': 1, 'B': 2, 'intensity': 3, 'rg_intensity': 4, 'rb_intensity': 5, 'gb_intensity': 6 };
      const channelA = channelOrder[metaA.channel] !== undefined ? channelOrder[metaA.channel] : 99;
      const channelB = channelOrder[metaB.channel] !== undefined ? channelOrder[metaB.channel] : 99;
      const channelResult = channelA - channelB;

      return channelResult;
    });

    return [...sampleCols, ...classCols, ...dataCols];
  }, [columns_metadata]);

  const orderedColumns = getOrderedColumns();

  // Track CTRL key state
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Control' || e.key === 'Meta') {
        setCtrlPressed(true);
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'Control' || e.key === 'Meta') {
        setCtrlPressed(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, []);

  // Auto-scroll functionality
  const startAutoScroll = (direction) => {
    if (autoScrollRef.current) {
      clearInterval(autoScrollRef.current);
    }

    const scrollSpeed = 30; // pixels per interval
    const scrollInterval = 50; // milliseconds

    autoScrollRef.current = setInterval(() => {
      if (!scrollContainerRef.current) return;

      const container = scrollContainerRef.current;
      const { scrollLeft, scrollTop } = container;

      switch (direction) {
        case 'up':
          container.scrollTop = Math.max(0, scrollTop - scrollSpeed);
          break;
        case 'down':
          container.scrollTop = scrollTop + scrollSpeed;
          break;
        case 'left':
          container.scrollLeft = Math.max(0, scrollLeft - scrollSpeed);
          break;
        case 'right':
          container.scrollLeft = scrollLeft + scrollSpeed;
          break;
        default:
          break;
      }

      // Update selection while scrolling
      if (isSelecting && selectionStart) {
        updateSelectionDuringScroll();
      }
    }, scrollInterval);
  };

  const stopAutoScroll = () => {
    if (autoScrollRef.current) {
      clearInterval(autoScrollRef.current);
      autoScrollRef.current = null;
    }
  };

  const updateSelectionDuringScroll = () => {
    if (!scrollContainerRef.current || !selectionStart) return;

    const container = scrollContainerRef.current;
    const containerRect = container.getBoundingClientRect();

    if (selectionEnd) {
      handleMouseEnter(selectionEnd.row, selectionEnd.col);
    }
  };

  const checkAutoScroll = (clientX, clientY) => {
    if (!scrollContainerRef.current || !isSelecting) return;

    const container = scrollContainerRef.current;
    const containerRect = container.getBoundingClientRect();
    const scrollMargin = 50; // pixels from edge to start scrolling

    // Check if near edges
    const nearTop = clientY - containerRect.top < scrollMargin;
    const nearBottom = containerRect.bottom - clientY < scrollMargin;
    const nearLeft = clientX - containerRect.left < scrollMargin;
    const nearRight = containerRect.right - clientX < scrollMargin;

    // Stop any existing auto-scroll
    stopAutoScroll();

    // Start auto-scroll in appropriate direction
    if (nearTop) {
      startAutoScroll('up');
    } else if (nearBottom) {
      startAutoScroll('down');
    } else if (nearLeft) {
      startAutoScroll('left');
    } else if (nearRight) {
      startAutoScroll('right');
    }
  };

  // Handle mouse move for auto-scroll detection
  const handleMouseMove = useCallback((e) => {
    if (isSelecting) {
      checkAutoScroll(e.clientX, e.clientY);
    }
  }, [isSelecting]);

  // Handle cell editing
  const handleCellClick = (rowIndex, columnName, event) => {
    // Don't start editing if right-clicking
    if (event.button === 2) return;

    // In range selection mode, don't allow single clicks on non-data columns
    if (isRangeSelectionMode) {
      const metadata = columns_metadata[columnName];
      if (metadata?.type !== 'data') {
        return; // Prevent selection on non-data columns
      }
    }

    if (editingCell) {
      handleSaveEdit();
    }

    // In range selection mode with CTRL, don't clear existing selections
    if (!isRangeSelectionMode || !ctrlPressed) {
      clearSelection();
    }

    const currentValue = data_matrix[rowIndex]?.[columnName];
    setEditingCell({ row: rowIndex, col: columnName });
    setEditValue(currentValue !== undefined ? currentValue.toString() : '');
  };

  const handleSaveEdit = () => {
    if (editingCell) {
      const { row, col } = editingCell;
      const currentValue = data_matrix[row]?.[col];

      // Convert value based on column type
      let newValue = editValue;
      const metadata = columns_metadata[col];

      if (metadata?.type === 'data' && editValue !== '') {
        newValue = parseFloat(editValue) || 0;
      }

      if (newValue !== currentValue) {
        const selectedRange = {
          startRow: row,
          endRow: row,
          columnNames: [col]
        };
        onCellsUpdate(selectedRange, [[newValue]]);
      }

      setEditingCell(null);
      setEditValue('');
    }
  };

  const handleCancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleMouseDown = (e, rowIndex, columnName) => {
    if (e.button === 0) { // Left mouse button
      e.preventDefault();

      // In range selection mode, only allow selection on data columns
      if (isRangeSelectionMode) {
        const metadata = columns_metadata[columnName];
        if (metadata?.type !== 'data') {
          return; // Prevent selection on non-data columns
        }
      }

      // Clear any active editing cell when starting selection
      if (editingCell) {
        handleSaveEdit();
      }

      // In range selection mode without CTRL, clear existing selections
      if (isRangeSelectionMode && !ctrlPressed) {
        clearSelection();
      }

      setIsSelecting(true);
      setSelectionStart({ row: rowIndex, col: columnName });
      setSelectionEnd({ row: rowIndex, col: columnName });
      setContextMenu(null); // Hide context menu
    }
  };

  const handleMouseEnter = (rowIndex, columnName) => {
    if (isSelecting && selectionStart) {
      // In range selection mode, only allow extending to data columns
      if (isRangeSelectionMode) {
        const metadata = columns_metadata[columnName];
        if (metadata?.type !== 'data') {
          return; // Don't extend selection to non-data columns
        }
      }

      setSelectionEnd({ row: rowIndex, col: columnName });
    }
  };

  // Modified handleMouseUp to handle multiple range selection
  const handleMouseUp = () => {
    stopAutoScroll(); // Stop any auto-scrolling

    if (isSelecting) {
      setIsSelecting(false);
      if (selectionStart && selectionEnd) {
        const newRange = calculateRange(selectionStart, selectionEnd);

        if (isRangeSelectionMode) {
          if (ctrlPressed) {
            // Add to existing selection
            setSelectedRanges(prev => [...prev, newRange]);
          } else {
            // Replace selection
            setSelectedRanges([newRange]);
          }

          // Call the callback with all selected ranges
          if (onRangeSelection) {
            const currentRanges = ctrlPressed ? [...selectedRanges, newRange] : [newRange];
            onRangeSelection(currentRanges);
          }
        } else {
          // Normal mode - single selection
          setSelectedRanges([newRange]);
        }
      }
    }
  };

  const calculateRange = (start, end) => {
    const startRow = Math.min(start.row, end.row);
    const endRow = Math.max(start.row, end.row);

    const startColIndex = Math.min(
      orderedColumns.indexOf(start.col),
      orderedColumns.indexOf(end.col)
    );
    const endColIndex = Math.max(
      orderedColumns.indexOf(start.col),
      orderedColumns.indexOf(end.col)
    );

    // Get all column names in the selected range
    const columnNames = [];
    for (let col = startColIndex; col <= endColIndex; col++) {
      const columnName = orderedColumns[col];

      // In range selection mode, only include data columns
      if (isRangeSelectionMode) {
        const metadata = columns_metadata[columnName];
        if (metadata?.type === 'data') {
          columnNames.push(columnName);
        }
      } else {
        // In normal mode, include all columns
        columnNames.push(columnName);
      }
    }

    return {
      startRow,
      endRow,
      columnNames,
      startColIndex,
      endColIndex
    };
  };

  const isCellSelected = (rowIndex, columnName) => {
    if (selectedRanges.length === 0) return false;

    // Check if cell is in any of the selected ranges
    return selectedRanges.some(range =>
      rowIndex >= range.startRow &&
      rowIndex <= range.endRow &&
      range.columnNames.includes(columnName)
    );
  };

  // Check if cell is in the current selection during drag
  const isCellSelecting = (rowIndex, columnName) => {
    if (!isSelecting || !selectionStart || !selectionEnd) return false;

    const currentRange = calculateRange(selectionStart, selectionEnd);
    return (
      rowIndex >= currentRange.startRow &&
      rowIndex <= currentRange.endRow &&
      currentRange.columnNames.includes(columnName)
    );
  };

  const isCellEditing = (rowIndex, columnName) => {
    return editingCell && editingCell.row === rowIndex && editingCell.col === columnName;
  };

  // Context menu handling
  const handleContextMenu = (e, rowIndex, columnName) => {
    e.preventDefault();

    // If right-clicking on a non-selected cell, select just that cell
    if (selectedRanges.length === 0 || !isCellSelected(rowIndex, columnName)) {
      const colIndex = orderedColumns.indexOf(columnName);
      const singleRange = {
        startRow: rowIndex,
        endRow: rowIndex,
        columnNames: [columnName],
        startColIndex: colIndex,
        endColIndex: colIndex
      };
      setSelectedRanges([singleRange]);
    }

    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      visible: true
    });
  };

  const hideContextMenu = () => {
    setContextMenu(null);
  };

  // Clipboard operations (updated for multiple ranges)
  const handleCopy = async () => {
    if (selectedRanges.length === 0) return;

    // For now, copy only the first range to maintain compatibility
    // You might want to enhance this to handle multiple ranges
    const selectedRange = selectedRanges[0];
    const copiedData = [];
    for (let row = selectedRange.startRow; row <= selectedRange.endRow; row++) {
      const rowData = [];
      for (const columnName of selectedRange.columnNames) {
        const value = data_matrix[row]?.[columnName] ?? '';
        rowData.push(value);
      }
      copiedData.push(rowData);
    }

    setClipboard({
      data: copiedData,
      range: { ...selectedRange }
    });

    // Copy to system clipboard as tab-separated values
    const textData = copiedData.map(row => row.join('\t')).join('\n');
    try {
      await navigator.clipboard.writeText(textData);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      // Fallback: Use document.execCommand for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = textData;
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
      } catch (fallbackErr) {
        console.error('Fallback copy also failed:', fallbackErr);
      }
      document.body.removeChild(textArea);
    }

    hideContextMenu();
  };

  const handleCut = async () => {
    await handleCopy();
    handleDelete();
  };

  const handlePaste = async () => {
    if (selectedRanges.length === 0) return;

    // For now, paste only to the first range
    const selectedRange = selectedRanges[0];

    try {
      const clipboardText = await navigator.clipboard.readText();
      if (clipboardText) {
        const pastedData = clipboardText.split('\n').map(row => {
          if (row.trim() === '') return [];
          return row.split('\t');
        }).filter(row => row.length > 0);

        pasteData(pastedData, selectedRange);
      } else if (clipboard && clipboard.data) {
        pasteData(clipboard.data, selectedRange);
      }
    } catch (err) {
      console.error('Failed to read from clipboard:', err);
      if (clipboard && clipboard.data) {
        pasteData(clipboard.data, selectedRange);
      }
    }

    hideContextMenu();
  };

  const pasteData = (dataToPaste, targetRange) => {
    if (!targetRange || !dataToPaste || dataToPaste.length === 0) return;

    const startRow = targetRange.startRow;
    const updateValues = [];

    dataToPaste.forEach((rowData, rowOffset) => {
      const rowValues = [];
      rowData.forEach((value, colOffset) => {
        if (colOffset < targetRange.columnNames.length) {
          const columnName = targetRange.columnNames[colOffset];
          const metadata = columns_metadata[columnName];

          let newValue = value;
          if (metadata?.type === 'data') {
            if (value !== '' && value !== null && value !== undefined) {
              newValue = parseFloat(value) || 0;
            } else {
              newValue = '';
            }
          } else {
            newValue = value !== null && value !== undefined ? value : '';
          }
          rowValues.push(newValue);
        }
      });
      updateValues.push(rowValues);
    });

    const pasteRange = {
      startRow: targetRange.startRow,
      endRow: targetRange.startRow + updateValues.length - 1,
      columnNames: targetRange.columnNames.slice(0, updateValues[0]?.length || 0)
    };

    onCellsUpdate(pasteRange, updateValues);
  };

  const handleDelete = () => {
    if (selectedRanges.length === 0) return;

    // For now, delete only the first range
    const selectedRange = selectedRanges[0];
    const emptyValues = [];
    for (let row = selectedRange.startRow; row <= selectedRange.endRow; row++) {
      const rowValues = [];
      for (let col = 0; col < selectedRange.columnNames.length; col++) {
        rowValues.push('');
      }
      emptyValues.push(rowValues);
    }

    onCellsUpdate(selectedRange, emptyValues);
    hideContextMenu();
  };

  // Format cell value based on column type
  const formatCellValue = (value, columnName) => {
    if (value === null || value === undefined || value === '') return '';

    const metadata = columns_metadata[columnName];

    if (metadata?.type === 'data' && typeof value === 'number') {
      return value.toFixed(2);
    }

    return value.toString();
  };

  // Get column header info
  const getColumnHeader = (columnName) => {
    const metadata = columns_metadata[columnName];

    if (metadata?.type === 'sample_info') {
      return {
        title: columnName.replace('_', ' ').toUpperCase(),
        subtitle: 'Sample Info',
        className: 'sample-info-header'
      };
    } else if (metadata?.type === 'class') {
      return {
        title: metadata.class_type || columnName,
        subtitle: 'Classification',
        className: 'class-header'
      };
    } else if (metadata?.type === 'data') {
      return {
        title: `${metadata.filename || 'Unknown'}`,
        subtitle: metadata.channel,
        className: metadata.channel === 'intensity' ? 'ir-header' : 'rgb-header'
      };
    }

    return { title: columnName, subtitle: '', className: '' };
  };

  // Helper functions for Excel column notation
  const indexToExcelColumn = (index) => {
    let result = '';
    while (index >= 0) {
      result = String.fromCharCode(65 + (index % 26)) + result;
      index = Math.floor(index / 26) - 1;
    }
    return result;
  };

  // Get selected ranges as Excel-like references
  const getSelectedRangesReference = () => {
    if (selectedRanges.length === 0) return '';

    return selectedRanges.map(range => {
      const startCol = indexToExcelColumn(orderedColumns.indexOf(range.columnNames[0]));
      const endCol = indexToExcelColumn(orderedColumns.indexOf(range.columnNames[range.columnNames.length - 1]));
      const startRow = range.startRow + 1;
      const endRow = range.endRow + 1;

      if (range.startRow === range.endRow && range.columnNames.length === 1) {
        return `${startCol}${startRow}`;
      }

      return `${startCol}${startRow}:${endCol}${endRow}`;
    }).join(', ');
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedRanges([]);
    setSelectionStart(null);
    setSelectionEnd(null);
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey || e.metaKey) {
        switch (e.key) {
          case 'c':
            e.preventDefault();
            handleCopy();
            break;
          case 'x':
            e.preventDefault();
            handleCut();
            break;
          case 'v':
            e.preventDefault();
            handlePaste();
            break;
          default:
            break;
        }
      } else if (e.key === 'Delete') {
        e.preventDefault();
        handleDelete();
      } else if (e.key === 'Escape') {
        if (editingCell) {
          handleCancelEdit();
        }
        clearSelection();
        hideContextMenu();
        stopAutoScroll();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedRanges, clipboard]);

  // Handle mouse move for auto-scroll
  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      stopAutoScroll();
    };
  }, [handleMouseMove]);

  // Handle clicks outside table
  useEffect(() => {
    const handleClickOutside = (event) => {
      const contextMenu = document.querySelector('.context-menu');
      const isClickOutsideTable = tableRef.current && !tableRef.current.contains(event.target);
      const isClickOutsideMenu = contextMenu && !contextMenu.contains(event.target);

      if (isClickOutsideTable && isClickOutsideMenu) {
        if (editingCell) {
          handleSaveEdit();
        }
        if (!isRangeSelectionMode || !ctrlPressed) {
          clearSelection();
        }
        hideContextMenu();
        stopAutoScroll();
      }
    };

    const handleGlobalMouseUp = () => {
      if (isSelecting) {
        setIsSelecting(false);
        stopAutoScroll();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      stopAutoScroll();
    };
  }, [editingCell, isSelecting, isRangeSelectionMode, ctrlPressed]);

  // Add special styling for range selection mode
  const getCellClassName = (rowIndex, columnName, metadata, isSelected, isEditing) => {
    const isSelectingNow = isCellSelecting(rowIndex, columnName);

    let className = `
      data-cell
      ${metadata?.type}-cell
      ${isSelected ? 'selected' : ''}
      ${isSelectingNow ? 'selecting' : ''}
      ${isEditing ? 'editing' : ''}
      ${metadata?.channel === 'intensity' ? 'ir-cell' : ''}
    `;

    if (isRangeSelectionMode) {
      className += ' range-selection-mode';
      if (metadata?.type === 'data') {
        className += ' selectable-data-cell';
      }
    }

    return className;
  };

  // Don't render if no data
  if (!data_matrix || !columns_metadata || data_matrix.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-muted">No data available</p>
      </div>
    );
  }

  return (
    <div className={`data-table-container ${isRangeSelectionMode ? 'range-selection-active' : ''}`}>
      {/* Selection Info */}
      {selectedRanges.length > 0 && (
        <div className="selection-info">
          <Badge bg="primary" className="me-2">
            Selected: {getSelectedRangesReference()}
          </Badge>
          <Badge bg="secondary" className="me-2">
            {selectedRanges.length} range{selectedRanges.length > 1 ? 's' : ''}
          </Badge>
          {isRangeSelectionMode && (
            <Badge bg="info" className="me-2">
              Hold CTRL for multiple selection
            </Badge>
          )}
          <Button size="sm" variant="outline-secondary" onClick={clearSelection}>
            Clear Selection
          </Button>
        </div>
      )}

      {/* Table */}
      <div className="table-scroll-container" ref={scrollContainerRef}>
        <Table bordered hover size="sm" className="data-table" ref={tableRef}>
          <thead className="table-dark sticky-top">
            <tr>
              {/* Row number header */}
              <th className="row-number-header">#</th>

              {/* Column headers */}
              {orderedColumns.map((columnName, colIndex) => {
                const header = getColumnHeader(columnName);
                return (
                  <th key={columnName} className={`column-header ${header.className}`}>
                    <div className="header-content">
                      <div className="header-title">{header.title}</div>
                      {header.subtitle && (
                        <div className="header-subtitle">{header.subtitle}</div>
                      )}
                    </div>
                    <div className="column-letter">
                      {indexToExcelColumn(colIndex)}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody onMouseUp={handleMouseUp}>
            {data_matrix.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {/* Row number */}
                <td className="row-number">{rowIndex + 1}</td>

                {/* Data cells */}
                {orderedColumns.map((columnName) => {
                  const isSelected = isCellSelected(rowIndex, columnName);
                  const isEditing = isCellEditing(rowIndex, columnName);
                  const metadata = columns_metadata[columnName];
                  const cellValue = row[columnName];

                  return (
                    <td
                      key={`${rowIndex}-${columnName}`}
                      className={getCellClassName(rowIndex, columnName, metadata, isSelected, isEditing)}
                      onClick={(e) => handleCellClick(rowIndex, columnName, e)}
                      onMouseDown={(e) => handleMouseDown(e, rowIndex, columnName)}
                      onMouseEnter={() => handleMouseEnter(rowIndex, columnName)}
                      onContextMenu={(e) => handleContextMenu(e, rowIndex, columnName)}
                    >
                      {isEditing ? (
                        <Form.Control
                          size="sm"
                          type={metadata?.type === 'data' ? 'number' : 'text'}
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyDown={handleKeyPress}
                          onBlur={handleSaveEdit}
                          autoFocus
                          step={metadata?.type === 'data' ? '0.01' : undefined}
                        />
                      ) : (
                        <span className="cell-content">
                          {formatCellValue(cellValue, columnName)}
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onCopy={handleCopy}
          onCut={handleCut}
          onPaste={handlePaste}
          onDelete={handleDelete}
          canPaste={!!clipboard}
          onHide={hideContextMenu}
        />
      )}

      {/* Table Statistics */}
      <div className="table-stats">
        <small className="text-muted">
          {data_matrix.length} rows × {orderedColumns.length} columns |
          {Object.values(columns_metadata).filter(m => m.type === 'data').length} data channels |
          {new Set(data_matrix.map(row => row.sample_name)).size} unique samples
        </small>
      </div>
    </div>
  );
};

export default DataTable;